import React, {useCallback, useMemo, useState} from 'react'
import styled from 'styled-components'
import {
  GIFT_SHOP_MIN_RESEND_SIZE,
  GIFT_SHOP_RESEND_VIDEO_BUFFER_DURATION,
  GIFT_SHOP_RESEND_VIDEO_MAX_DURATION,
  GIFT_SHOP_RESEND_VIDEO_TYPE,
  GIFT_SHOP_UPLOAD_VIDEO_MIME_EXTENSION_MAP,
  GIFT_SHOP_UPLOAD_VIDEO_TYPE,
} from 'consts'
import {useTranslation} from 'i18n'
import {GiftShopRootScreen, GiftshopTemplateUploadResendModal} from 'pages'
import {GiftshopUploadContentData, VideoMetadata} from 'types'
import {
  getUniqueId,
  showSnackbar,
  useDebounce,
  useDidUpdate,
  useHistory,
} from 'utils'
import {Button, InputPicker} from 'common/components'
import convertUnit from 'lib/unit'
import {GiftShopResendRevisionContentVideo} from '../containers'
import {GiftShopResendRevisionScreenVideoProps} from './GiftShopResendRevisionProps'

const StyledContainer = styled.div`
  height: 100%;
`

const StyledTryAgain = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${convertUnit(20)};
  margin-top: ${convertUnit(20)};
`

const StyledButton = styled(Button)`
  width: 100%;
`

export default function GiftShopResendRevisionScreenVideo({
  collectorId,
  collectorNickname,
  contentId,
  invoiceId,
  username,
  stateRevisionData,
  stateLoadingImg,
  stateLoadingBtn,
  stateSuccess,
  stateResendModal,
  inputRef,
  uploadRevision,
}: GiftShopResendRevisionScreenVideoProps) {
  const {translate} = useTranslation()
  // const {username} = useSelector('user') || {}
  const history = useHistory()
  const [revisionData, setRevisionData] = stateRevisionData
  const [revisionFile, setRevisionFile] = useState<File>()
  const setResendModal = stateResendModal[1]
  const [success, setSuccess] = stateSuccess
  const debounce = useDebounce()
  const [loadingImg, setLoadingImg] = stateLoadingImg
  const [loadingBtn, setLoadingBtn] = stateLoadingBtn

  const handleUploadRevision = useCallback(async () => {
    setLoadingBtn(true)
    setResendModal('loading')
    const isSuccess =
      revisionData &&
      (await uploadRevision(
        revisionData.image,
        contentId,
        collectorId,
        invoiceId,
        'video',
      ))
    if (isSuccess) {
      setSuccess((prev) => prev + 1)
    } else {
      setResendModal('failed')
    }
    setLoadingBtn(false)
  }, [
    collectorId,
    contentId,
    invoiceId,
    revisionData,
    setLoadingBtn,
    setResendModal,
    setSuccess,
    uploadRevision,
  ])

  const handleReadVideo = useCallback(
    async (file: File) => {
      if (!GIFT_SHOP_UPLOAD_VIDEO_TYPE.includes(file.type)) {
        showSnackbar(translate('giftShop:invalidFormatData'))
        setLoadingImg(false)
        return
      }

      if (file.size < GIFT_SHOP_MIN_RESEND_SIZE) {
        showSnackbar(translate('giftShop:resendMinSize'))
        setLoadingImg(false)
        return
      }

      setRevisionFile(file)
    },
    [setLoadingImg, translate],
  )

  const handleLoadFiles = useCallback(
    (files: File[]) => {
      setLoadingImg(true)
      Array.from(files).forEach((file) => {
        handleReadVideo(file)
      })
    },
    [handleReadVideo, setLoadingImg],
  )

  const handleNavigateProfile = useCallback(() => {
    history.replace('giftshop_profile', {self: true}, username)
  }, [history, username])

  const handleSetRevisionData = useCallback(
    (metadata: VideoMetadata, objectUrl: string) => {
      const maxDuration =
        GIFT_SHOP_RESEND_VIDEO_MAX_DURATION +
        GIFT_SHOP_RESEND_VIDEO_BUFFER_DURATION

      if (!revisionFile) return

      if (metadata.duration > maxDuration) {
        setRevisionData(undefined)
        setRevisionFile(undefined)
        showSnackbar(
          translate('giftShop:videoUploadErrorMaxDuration', {
            duration: Math.floor(maxDuration),
          }),
        )
        return
      }

      const image: GiftshopUploadContentData = {
        id: getUniqueId(),
        file: revisionFile,
        src: objectUrl,
        size: revisionFile.size,
        duration: Math.floor(metadata.duration),
        width: metadata.width,
        height: metadata.height,
        extension:
          GIFT_SHOP_UPLOAD_VIDEO_MIME_EXTENSION_MAP[revisionFile.type] ?? 'mp4',
      }

      setRevisionData({
        id: contentId,
        image,
      })
    },
    [contentId, revisionFile, setRevisionData, translate],
  )

  const handleRenderContent = useMemo(
    () => (
      <GiftShopResendRevisionContentVideo
        revisionData={revisionData}
        revisionFile={revisionFile}
        loadingImg={loadingImg}
        onAddPhoto={() => inputRef.current?.click()}
        onUpload={handleUploadRevision}
        loadingBtn={loadingBtn}
        onVideoLoaded={handleSetRevisionData}
      />
    ),
    [
      handleSetRevisionData,
      handleUploadRevision,
      inputRef,
      loadingBtn,
      loadingImg,
      revisionData,
      revisionFile,
    ],
  )

  const handleRenderModalBottomSection = useMemo(
    () => (
      <StyledTryAgain>
        <StyledButton
          label={translate('global:close')}
          color="primary_5"
          backgroundColor="white_2"
          onClick={handleNavigateProfile}
        />
        <StyledButton
          label={translate('global:tryAgain')}
          onClick={() => setResendModal('none')}
        />
      </StyledTryAgain>
    ),
    [handleNavigateProfile, setResendModal, translate],
  )

  const handleRenderInputPicker = useMemo(
    () => (
      <InputPicker
        inputRef={inputRef}
        onLoadFiles={handleLoadFiles}
        accept={GIFT_SHOP_RESEND_VIDEO_TYPE.join(',')}
      />
    ),
    [handleLoadFiles, inputRef],
  )

  const handleRenderModalResendStatus = useMemo(
    () => (
      <GiftshopTemplateUploadResendModal
        resendModalState={stateResendModal}
        bottomElement={handleRenderModalBottomSection}
        success={success}
        total={1}
      />
    ),
    [handleRenderModalBottomSection, stateResendModal, success],
  )

  useDidUpdate(() => {
    if (success === 1) {
      setResendModal('success')
      debounce(() => {
        handleNavigateProfile()
      }, 500)
    }
  }, [success, history])

  return (
    <GiftShopRootScreen
      showBadgeIcon={false}
      headerTitle={translate('giftShop:resendRevisionButton')}
      headerSubtitle={translate('global:forUser', {
        nickname: collectorNickname,
      })}>
      {handleRenderInputPicker}
      {handleRenderModalResendStatus}
      <StyledContainer>{handleRenderContent}</StyledContainer>
    </GiftShopRootScreen>
  )
}
